import { askConfirm } from '../lib/askConfirm';
import { mPerLat, mPerLon } from '../lib/geo';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { newPit, rectFromCorners, rectFromDims, setProjectPitPrices } from '../lib/excavation';
import { haversine } from '../lib/model';

export default function usePits({ t, project, updateProject, net, repId, mapRef }) {
  const [editing, setEditing] = useState(null);
  const pits = project?.pits || [];
  const draft = net.draft;
  const nextName = () => `${t.pitDefault} ${pits.length + 1}`;

  const openNew = useCallback((geo) => {
    net.setDraft(null);
    setEditing({ ...newPit({ ...geo, name: nextName(), reportId: repId }), isNew: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net.setDraft, repId, pits.length]);

  const finish = () => {
    const d = net.draftRef.current;
    if (!d || d.kind !== 'pit') return;
    if (d.garden) {
      const [a, b] = d.points;
      if (d.shape === 'polygon' && d.points.length < 3) return toast.error(t.pitNeed3);
      const r = d.shape === 'circle' && haversine(a, b);
      const pts = d.shape === 'polygon' ? d.points : d.shape === 'rect' ? rectFromCorners([a, b]) : Array.from({ length: 32 }, (_, i) => [a[0] + (r * Math.sin(i / 5.093)) / mPerLat(a[0]), a[1] + (r * Math.cos(i / 5.093)) / mPerLon(a[0])]);
      net.setDraft(null);
      return window.dispatchEvent(new CustomEvent('giss-garden-area', { detail: pts }));
    }
    if (d.shape === 'polygon') {
      if (d.points.length < 3) return toast.error(t.pitNeed3);
      return openNew({ shape: 'polygon', pts: d.points });
    }
    const [a, b] = d.points;
    if (d.shape === 'rect') return openNew({ shape: 'rect', pts: rectFromCorners([a, b]) });
    return openNew({ shape: 'circle', center: a, r: Math.max(0.1, haversine(a, b)) });
  };

  useEffect(() => {
    if (draft?.kind === 'pit' && draft.shape !== 'polygon' && draft.points.length === 2 && !draft.dragging) finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const fromDims = (a, b) => {
    const c = mapRef.current.getCenter();
    const shape = net.draftRef.current?.shape;
    if (!(+a > 0) || (shape === 'rect' && !(+b > 0))) return toast.error(t.pitInvalid);
    if (shape === 'circle') return openNew({ shape: 'circle', center: [c.lat, c.lng], r: +a / 2 });
    const pit = { shape: 'rect', pts: rectFromDims([c.lat, c.lng], +a, +b) };
    return openNew(pit);
  };

  const save = (pit) => {
    const { isNew, ...clean } = pit;
    updateProject((p) => ({ ...p, pits: isNew ? [...(p.pits || []), clean] : (p.pits || []).map((x) => (x.id === clean.id ? clean : x)) }));
    setEditing(null);
    toast.success(t.pitSaved);
  };
  const remove = async (id) => {
    if (!(await askConfirm(t.pitConfirmDel, { ok: t.pitDelete, cancel: t.btnCancel }))) return false;
    updateProject((p) => ({ ...p, pits: (p.pits || []).filter((x) => x.id !== id) }));
    setEditing(null);
    return true;
  };
  const open = useCallback((id) => {
    const pit = (project?.pits || []).find((x) => x.id === id);
    if (pit) setEditing({ ...pit });
  }, [project]);

  const setProjPitPrice = (mat, next) => {
    const cur = project?.prices?.pits || {};
    const pp = { ...cur, [mat]: { ...cur[mat], ...next } };
    setProjectPitPrices(pp);
    updateProject((p) => ({ ...p, prices: { ...(p.prices || {}), pits: pp } }));
  };
  const patch = (id, next) => updateProject((p) => ({ ...p, pits: (p.pits || []).map((x) => (x.id === id ? { ...x, ...next } : x)) }));
  return { pits, patch, setProjPitPrice, editing, setEditing, start: (shape = 'rect', garden = false) => net.setDraft({ kind: 'pit', shape, points: [], garden }), finish, fromDims, save, remove, open };
}
